export const STAGES = ["avatar", "identity", "knowledge", "interview", "distill", "review", "publish", "assign"] as const;
export type Stage = (typeof STAGES)[number];
export type StageStatus = "locked" | "open" | "done";

/** Everything the gates need to know, computed by the store in one query. */
export type PipelineState = {
  avatarPinned: boolean;
  identitySet: boolean;
  sourcesPending: number;
  sourcesReady: number;
  interviewsEnded: number;
  draftRevisionId?: string;
  /** A source or interview finished after the draft was distilled. */
  draftStale: boolean;
  reviewed: boolean;
  /** The pinned studio release still resolves. */
  avatarLive: boolean;
  releases: number;
  /** Tenant availability has been set at least once. */
  assigned: boolean;
};

export class GateError extends Error {
  readonly status = 409;
}

export type GatedAction = "distill" | "review" | "publish" | "assign" | "interview" | "knowledge";

export function assertCan(s: PipelineState, action: GatedAction): void {
  const refuse = (why: string) => {
    throw new GateError(why);
  };
  if (action === "knowledge" || action === "interview") {
    if (!s.avatarPinned) refuse("Pick a published avatar first.");
    if (!s.identitySet) refuse("Set the employee's name and job title first.");
    return;
  }
  if (action === "distill") {
    if (s.interviewsEnded < 1) refuse("Finish at least one interview before distilling.");
    if (s.sourcesPending > 0) refuse(`${s.sourcesPending} knowledge source(s) are still being read.`);
    return;
  }
  if (action === "review") {
    if (!s.draftRevisionId) refuse("There is no distilled draft to review yet.");
    return;
  }
  if (action === "publish") {
    if (!s.draftRevisionId) refuse("There is no distilled draft to publish.");
    if (s.draftStale) refuse("The draft is stale: new knowledge or interviews arrived after it was distilled. Distill again.");
    if (s.sourcesPending > 0) refuse(`${s.sourcesPending} knowledge source(s) are still being read. Wait for them, then distill again.`);
    if (!s.reviewed) refuse("The draft needs a review before publishing.");
    if (!s.avatarLive) refuse("The pinned avatar release is no longer published in the studio. Pick another avatar.");
    return;
  }
  if (action === "assign") {
    if (s.releases < 1) refuse("Publish a release before assigning tenants.");
  }
}

function allowed(s: PipelineState, a: GatedAction): boolean {
  try {
    assertCan(s, a);
    return true;
  } catch {
    return false;
  }
}

export function stageStatus(s: PipelineState): Record<Stage, StageStatus> {
  const st = (done: boolean, open: boolean): StageStatus => (done ? "done" : open ? "open" : "locked");
  return {
    avatar: st(s.avatarPinned, true),
    identity: st(s.identitySet, s.avatarPinned),
    knowledge: st(s.sourcesReady > 0 && s.sourcesPending === 0, allowed(s, "knowledge")),
    interview: st(s.interviewsEnded > 0, allowed(s, "interview")),
    distill: st(Boolean(s.draftRevisionId) && !s.draftStale, allowed(s, "distill")),
    review: st(s.reviewed && !s.draftStale, allowed(s, "review")),
    publish: st(s.releases > 0 && !s.draftStale && s.reviewed, allowed(s, "publish")),
    assign: st(s.assigned && s.releases > 0, allowed(s, "assign")),
  };
}
